import imageCompression from 'browser-image-compression';

export const TARGET_IMAGE_WIDTH = 2000;
export const TARGET_IMAGE_HEIGHT = 2000;
export const TARGET_IMAGE_SIZE_BYTES = 500 * 1024;

export const getCropCoordinates = (crop, imageWidth, imageHeight) => {
  const safeCrop = {
    x: Number.isFinite(crop.x) ? crop.x : 0,
    y: Number.isFinite(crop.y) ? crop.y : 0,
    width: Number.isFinite(crop.width) ? crop.width : 100,
    height: Number.isFinite(crop.height) ? crop.height : 100,
  };

  const x = clampPercentage(safeCrop.x, 0, 100);
  const y = clampPercentage(safeCrop.y, 0, 100);
  const width = clampPercentage(safeCrop.width, 1, 100 - x);
  const height = clampPercentage(safeCrop.height, 1, 100 - y);

  const cropX = Math.round((x / 100) * imageWidth);
  const cropY = Math.round((y / 100) * imageHeight);
  const cropWidth = Math.round((width / 100) * imageWidth);
  const cropHeight = Math.round((height / 100) * imageHeight);

  return {
    x: clamp(cropX, 0, imageWidth - 1),
    y: clamp(cropY, 0, imageHeight - 1),
    width: clamp(cropWidth, 1, imageWidth - clamp(cropX, 0, imageWidth - 1)),
    height: clamp(cropHeight, 1, imageHeight - clamp(cropY, 0, imageHeight - 1)),
  };
};

export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
export const clampPercentage = (value, min, max) => clamp(Number(value) || 0, min, max);

export const createTargetImageFile = async (file, crop, options = {}) => {
  const targetWidth = Number(options.width) || TARGET_IMAGE_WIDTH;
  const targetHeight = Number(options.height) || TARGET_IMAGE_HEIGHT;
  const maxSizeMB = Number(options.maxSizeMB) || (Number(options.maxSizeBytes) ? Number(options.maxSizeBytes) / (1024 * 1024) : 0.5);
  const maxBytes = Number(options.maxSizeBytes) || (maxSizeMB * 1024 * 1024);
  const compressionPercent = Number.isFinite(Number(options.compressionPercent))
    ? clampPercentage(Number(options.compressionPercent), 10, 100)
    : null;

  const imageUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(imageUrl);
    const cropRect = getCropCoordinates(crop, image.naturalWidth || image.width, image.naturalHeight || image.height);

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = cropRect.width;
    cropCanvas.height = cropRect.height;
    const cropContext = cropCanvas.getContext('2d');

    if (!cropContext) {
      throw new Error('Unable to read image context for cropping.');
    }

    cropContext.drawImage(
      image,
      cropRect.x,
      cropRect.y,
      cropRect.width,
      cropRect.height,
      0,
      0,
      cropRect.width,
      cropRect.height
    );

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = targetWidth;
    outputCanvas.height = targetHeight;

    const outputContext = outputCanvas.getContext('2d');
    if (!outputContext) {
      throw new Error('Unable to create preview canvas.');
    }

    outputContext.fillStyle = '#ffffff';
    outputContext.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
    outputContext.drawImage(cropCanvas, 0, 0, targetWidth, targetHeight);

    const initialQuality = compressionPercent === null ? 0.85 : Math.max(0.1, compressionPercent / 100);
    const croppedBlob = await canvasBlobFromCanvas(outputCanvas, 'image/jpeg', initialQuality);
    const compressedBlob = await compressBlobForTarget(croppedBlob, maxBytes, targetWidth, targetHeight, compressionPercent);
    const processedFile = new File([compressedBlob], `${file.name.replace(/\.[^.]+$/, '') || 'product-image'}.jpg`, {
      type: 'image/jpeg',
    });

    return {
      file: processedFile,
      previewUrl: URL.createObjectURL(compressedBlob),
      width: targetWidth,
      height: targetHeight,
      size: compressedBlob.size,
    };
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};

const compressBlobForTarget = async (blob, maxBytes, targetWidth, targetHeight, compressionPercent) => {
  const upperQualityLimit = compressionPercent === null ? 0.85 : clampPercentage(compressionPercent, 10, 100) / 100;

  const compressed = await imageCompression(blob, {
    maxSizeMB: Math.min(maxBytes / (1024 * 1024), 2),
    maxWidthOrHeight: Math.max(targetWidth, targetHeight),
    useWebWorker: true,
    initialQuality: upperQualityLimit,
    fileType: 'image/jpeg',
  });

  if (compressed.size <= maxBytes) {
    return compressed;
  }

  const fallbackQuality = compressionPercent === null ? 0.55 : Math.max(0.1, upperQualityLimit - 0.15);
  return await imageCompression(blob, {
    maxSizeMB: Math.max(0.1, maxBytes / (1024 * 1024)),
    maxWidthOrHeight: Math.max(targetWidth, targetHeight),
    useWebWorker: true,
    initialQuality: fallbackQuality,
    fileType: 'image/jpeg',
  });
};

const canvasBlobFromCanvas = async (canvas, mimeType, quality) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to generate a cropped image.'));
        return;
      }
      resolve(blob);
    }, mimeType, quality);
  });
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Unable to load selected image.'));
  img.src = src;
});
